import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('system');
  const [isLoading, setIsLoading] = useState(true);

  // Fonction pour appliquer le thème au document
  const applyTheme = (themePreference) => {
    const html = document.documentElement;
    
    console.log('Application du thème:', themePreference);
    
    // Supprimer les classes existantes
    html.classList.remove('light', 'dark');
    
    if (themePreference === 'dark') {
      html.classList.add('dark');
      console.log('Classe dark appliquée');
    } else if (themePreference === 'light') {
      html.classList.add('light');
      console.log('Classe light appliquée');
    } else {
      // Mode système - utiliser les préférences système
      const isSystemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      console.log('Mode système - préférence sombre:', isSystemDark);
      if (isSystemDark) {
        html.classList.add('dark');
      } else {
        html.classList.add('light');
      }
    }
    
    console.log('Classes HTML après application:', html.className);
  };

  // Fonction pour déterminer le type d'utilisateur et la collection appropriée
  const getUserCollectionInfo = async (user) => {
    if (!user || !user.email) return null;

    // Vérifier d'abord si c'est un bénéficiaire (nouveau système)
    // Les bénéficiaires sont stockés dans employees
    try {
      const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        const isBeneficiary = employeeData.role === 'beneficiaire';

        console.log('Type d\'utilisateur détecté:', isBeneficiary ? 'Bénéficiaire' : 'Employé');
        console.log('Collection utilisée: employees');
        console.log('Email utilisateur:', user.email);

        return { isClientUser: isBeneficiary, collectionName: 'employees' };
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du type d\'utilisateur:', error);
    }

    // Vérifier l'ancien système (email avec préfixe client_)
    const isClientUser = user.email.startsWith('client_') && user.email.endsWith('@agentime.fr');
    const collectionName = isClientUser ? 'clients' : 'employees';

    console.log('Type d\'utilisateur détecté:', isClientUser ? 'Client (ancien système)' : 'Employé');
    console.log('Collection utilisée:', collectionName);
    console.log('Email utilisateur:', user.email);

    return { isClientUser, collectionName };
  };

  // Charger le thème depuis Firestore - écoute l'état d'auth pour éviter le cas où currentUser est null au montage
  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        console.log('Chargement du thème - Utilisateur:', user?.uid);

        if (user) {
          const userInfo = await getUserCollectionInfo(user);

          if (userInfo) {
            const { collectionName } = userInfo;
            const userDoc = await getDoc(doc(db, collectionName, user.uid));
            console.log('Document utilisateur trouvé:', userDoc.exists());

            if (userDoc.exists()) {
              const userData = userDoc.data();
              const savedTheme = userData.themePreference || 'system';
              console.log('Thème sauvegardé trouvé:', savedTheme);
              setTheme(savedTheme);
              applyTheme(savedTheme);
            } else {
              console.log('Document utilisateur non trouvé, utilisation du thème système');
              applyTheme('system');
            }
          } else {
            console.log('Informations utilisateur non disponibles, utilisation du thème système');
            applyTheme('system');
          }
        } else {
          console.log('Utilisateur non connecté, utilisation du thème système');
          setTheme('system');
          applyTheme('system');
        }
      } catch (error) {
        console.error('Erreur lors du chargement du thème:', error);
        applyTheme('system');
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Écouter les changements de préférences système
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = () => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [theme]);

  // Fonction pour changer le thème
  const changeTheme = async (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    
    // Sauvegarder dans Firestore si l'utilisateur est connecté
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const userInfo = await getUserCollectionInfo(user);
        if (userInfo) {
          const { collectionName } = userInfo;

          // Vérifier si le document existe avant de tenter la mise à jour
          const userDoc = await getDoc(doc(db, collectionName, user.uid));
          
          if (userDoc.exists()) {
            // Mettre à jour le document existant
            await updateDoc(doc(db, collectionName, user.uid), {
              themePreference: newTheme
            });
            console.log('Thème sauvegardé dans Firestore:', newTheme, 'Collection:', collectionName);
          } else {
            console.log('Document utilisateur non trouvé, création du champ themePreference impossible');
            // Pour les bénéficiaires, on pourrait créer le document si nécessaire
            // Mais pour l'instant on se contente de logger l'erreur
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du thème:', error);
      console.log('Détails de l\'erreur:', error.message);
    }
  };

  const value = {
    theme,
    changeTheme,
    isLoading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
